import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Pagination,
  TextField,
  InputAdornment,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from '@mui/material';
import {
  UploadFile as UploadFileIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useSnackbar } from '../contexts/SnackbarContext.tsx';
import {
  opentableImportsApi,
  OpenTableImportedReservation,
  PaginatedOpenTableImportsResponse,
  ImportSummary,
} from '../services/opentableImportsService.ts';
import { formatPhoneNumber } from '../utils/formatPhone.ts';

const headerCellSx = {
  color: '#fff',
  fontWeight: 700,
  fontSize: '0.78rem',
  letterSpacing: '0.04em',
  textTransform: 'uppercase' as const,
  border: '2px solid #7f0000',
  py: 1.5,
  px: 1.5,
  whiteSpace: 'nowrap' as const,
};

const emptyEditForm = {
  guestName: '',
  phone: '',
  email: '',
  partySize: '',
  reservationDate: '',
  reservationTime: '',
  tableName: '',
  status: '',
  occasion: '',
  specialRequests: '',
};

const OpenTableImportsPage: React.FC = () => {
  const { sessionToken, user } = useAuth();
  const isAdmin = user?.type === 'admin';
  const { showError, showSuccess } = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [reservations, setReservations] = useState<OpenTableImportedReservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [lastSummary, setLastSummary] = useState<ImportSummary | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<OpenTableImportedReservation | null>(null);
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [saving, setSaving] = useState(false);

  const fetchReservations = async (pageNum: number = 1, searchTerm?: string) => {
    if (!sessionToken) return;
    setLoading(true);
    try {
      const res: PaginatedOpenTableImportsResponse = await opentableImportsApi.getAll(
        { page: pageNum, limit, search: searchTerm },
        sessionToken
      );
      setReservations(res.data);
      setPage(res.page);
      setTotalPages(res.totalPages);
      setTotal(res.total);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to load imported reservations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReservations();
  }, [sessionToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    fetchReservations(value, search || undefined);
  };

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
    fetchReservations(1, searchInput || undefined);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
    fetchReservations(1);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !sessionToken) return;

    setUploading(true);
    setLastSummary(null);
    try {
      const summary = await opentableImportsApi.uploadCsv(file, sessionToken);
      setLastSummary(summary);
      if (summary.imported > 0) {
        showSuccess(`Imported ${summary.imported} reservation${summary.imported !== 1 ? 's' : ''} (party size 15+).`);
      } else {
        showError('No reservations with party size 15+ were found in that file.');
      }
      fetchReservations(1, search || undefined);
      setPage(1);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to import CSV');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (id: number) => {
    if (!sessionToken || !window.confirm('Delete this imported reservation?')) return;
    try {
      await opentableImportsApi.remove(id, sessionToken);
      showSuccess('Reservation deleted.');
      fetchReservations(page, search || undefined);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete reservation');
    }
  };

  const formatDate = (dateString: string | null) =>
    dateString ? new Date(dateString.slice(0, 10) + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

  const openEdit = (r: OpenTableImportedReservation) => {
    setEditingRow(r);
    setEditForm({
      guestName: r.guestName || '',
      phone: r.phone || '',
      email: r.email || '',
      partySize: r.partySize.toString(),
      reservationDate: r.reservationDate ? r.reservationDate.slice(0, 10) : '',
      reservationTime: r.reservationTime || '',
      tableName: r.tableName || '',
      status: r.status || '',
      occasion: r.occasion || '',
      specialRequests: r.specialRequests || '',
    });
    setEditDialogOpen(true);
  };

  const handleUpdateSave = async () => {
    if (!sessionToken || !editingRow) return;
    const partySize = parseInt(editForm.partySize, 10);
    if (!partySize || partySize < 1) {
      showError('Party size must be a valid number.');
      return;
    }

    setSaving(true);
    try {
      await opentableImportsApi.update(
        editingRow.id,
        {
          guestName: editForm.guestName.trim() || undefined,
          phone: editForm.phone.trim() || undefined,
          email: editForm.email.trim() || undefined,
          partySize,
          reservationDate: editForm.reservationDate || undefined,
          reservationTime: editForm.reservationTime.trim() || undefined,
          tableName: editForm.tableName.trim() || undefined,
          status: editForm.status.trim() || undefined,
          occasion: editForm.occasion.trim() || undefined,
          specialRequests: editForm.specialRequests.trim() || undefined,
        },
        sessionToken
      );
      showSuccess('Reservation updated.');
      setEditDialogOpen(false);
      fetchReservations(page, search || undefined);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update reservation');
    } finally {
      setSaving(false);
    }
  };

  const escapeHtml = (value: string): string =>
    value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const handlePrint = (r: OpenTableImportedReservation) => {
    const rows: [string, string][] = [
      ['Guest', r.guestName || '—'],
      ['Email', r.email || '—'],
      ['Phone', formatPhoneNumber(r.phone) || '—'],
      ['Party Size', String(r.partySize)],
      ['Date', r.reservationDate ? formatDate(r.reservationDate) : (r.reservationDateRaw || '—')],
      ['Time', r.reservationTime || '—'],
      ['Table', r.tableName || '—'],
      ['Status', r.status || '—'],
      ['Occasion', r.occasion || '—'],
      ['Special Requests', r.specialRequests || '—'],
    ];

    const html = `
      <html>
        <head>
          <title>Reservation - ${escapeHtml(r.guestName || 'Guest')}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 32px; color: #212121; }
            h1 { font-size: 20px; margin-bottom: 4px; }
            .subtitle { color: #666; margin-bottom: 24px; font-size: 13px; }
            table { border-collapse: collapse; width: 100%; max-width: 480px; }
            td { padding: 8px 12px; border: 1px solid #ddd; font-size: 14px; }
            td:first-child { font-weight: 600; background: #fafafa; width: 160px; }
          </style>
        </head>
        <body>
          <h1>OpenTable Reservation</h1>
          <div class="subtitle">Imported party size 15+ reservation</div>
          <table>
            ${rows.map(([label, value]) => `<tr><td>${escapeHtml(label)}</td><td>${escapeHtml(value)}</td></tr>`).join('')}
          </table>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.onload = () => {
        setTimeout(() => printWindow.print(), 300);
      };
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" sx={{ color: "#7f0000", fontWeight: 700, flex: "0 0 auto", whiteSpace: "nowrap" }}>
          Date: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
        </Typography>
        <Typography variant="h4" component="h1" sx={{ flex: 1, textAlign: 'center', fontWeight: 700, textTransform: 'uppercase' }}>
          OpenTable Imports
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchReservations(page, search || undefined)}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
            onClick={handleUploadClick}
            disabled={uploading}
            sx={{ backgroundColor: '#b71c1c', '&:hover': { backgroundColor: '#7f0000' } }}
          >
            {uploading ? 'Importing...' : 'Upload CSV'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={handleFileSelected}
          />
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Import reservations exported from OpenTable Guest Center. Only rows with party size 15+ are kept.
      </Typography>

      {lastSummary && (
        <Alert
          severity={lastSummary.imported > 0 ? 'success' : 'warning'}
          sx={{ mb: 3 }}
          onClose={() => setLastSummary(null)}
        >
          <AlertTitle>Import Results</AlertTitle>
          {lastSummary.totalRows} row{lastSummary.totalRows !== 1 ? 's' : ''} read &middot; {lastSummary.imported} imported (15+) &middot; {lastSummary.skippedTooSmall} skipped (under 15) &middot; {lastSummary.skippedInvalid} skipped (unreadable party size)
          {lastSummary.errors.length > 0 && (
            <List dense sx={{ mt: 1 }}>
              {lastSummary.errors.slice(0, 10).map((e, i) => (
                <ListItem key={i} sx={{ py: 0 }}>
                  <ListItemText primaryTypographyProps={{ variant: 'caption' }} primary={e} />
                </ListItem>
              ))}
              {lastSummary.errors.length > 10 && (
                <ListItem sx={{ py: 0 }}>
                  <ListItemText primaryTypographyProps={{ variant: 'caption' }} primary={`...and ${lastSummary.errors.length - 10} more`} />
                </ListItem>
              )}
            </List>
          )}
        </Alert>
      )}

      <Paper sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            fullWidth
            size="small"
            label="Search by name or email"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchInput && (
                <InputAdornment position="end">
                  <Button size="small" onClick={handleClearSearch} sx={{ minWidth: 'auto', p: 0.5 }}>
                    <ClearIcon fontSize="small" />
                  </Button>
                </InputAdornment>
              ),
            }}
          />
          <Button variant="contained" onClick={handleSearch} disabled={loading} sx={{ minWidth: '100px' }}>
            Search
          </Button>
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden', border: '2px solid #9e9e9e' }}>
          <TableContainer>
            <Table sx={{ borderCollapse: 'collapse', '& td': { border: '2px solid #9e9e9e', py: 1.25, px: 1.5 } }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#b71c1c' }}>
                  <TableCell sx={headerCellSx}>Guest</TableCell>
                  <TableCell sx={headerCellSx}>Email</TableCell>
                  <TableCell sx={headerCellSx}>Phone</TableCell>
                  <TableCell sx={headerCellSx}>Party Size</TableCell>
                  <TableCell sx={headerCellSx}>Date</TableCell>
                  <TableCell sx={headerCellSx}>Time</TableCell>
                  <TableCell sx={headerCellSx}>Table</TableCell>
                  <TableCell sx={headerCellSx}>Status</TableCell>
                  <TableCell sx={headerCellSx}>Occasion</TableCell>
                  <TableCell sx={headerCellSx}>Imported</TableCell>
                  <TableCell align="center" sx={headerCellSx}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reservations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 5, border: '2px solid #9e9e9e', color: 'text.secondary' }}>
                      {search ? 'No imported reservations match your search.' : 'No reservations imported yet. Click "Upload CSV" to get started.'}
                    </TableCell>
                  </TableRow>
                )}
                {reservations.map((r, idx) => (
                  <TableRow
                    key={r.id}
                    sx={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fdf5f5', '&:hover': { backgroundColor: '#fce4e4' } }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>{r.guestName || '—'}</TableCell>
                    <TableCell>{r.email || '—'}</TableCell>
                    <TableCell>{formatPhoneNumber(r.phone) || '—'}</TableCell>
                    <TableCell>{r.partySize}</TableCell>
                    <TableCell>{r.reservationDate ? formatDate(r.reservationDate) : (r.reservationDateRaw || '—')}</TableCell>
                    <TableCell>{r.reservationTime || '—'}</TableCell>
                    <TableCell>{r.tableName || '—'}</TableCell>
                    <TableCell>{r.status || '—'}</TableCell>
                    <TableCell>{r.occasion || '—'}</TableCell>
                    <TableCell>{new Date(r.importedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</TableCell>
                    <TableCell align="center">
                      {isAdmin ? (
                        <>
                          <Tooltip title="Update">
                            <IconButton size="small" color="primary" onClick={() => openEdit(r)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Print">
                            <IconButton size="small" color="default" onClick={() => handlePrint(r)}>
                              <PrintIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleDelete(r.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <Tooltip title="Only admins can update, print, or delete">
                          <span>
                            <IconButton size="small" disabled>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {reservations.length} of {total} reservation{total !== 1 ? 's' : ''}
              </Typography>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Paper>
      )}

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Reservation</DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Guest Name"
                fullWidth
                value={editForm.guestName}
                onChange={e => setEditForm(f => ({ ...f, guestName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Party Size"
                type="number"
                fullWidth
                required
                inputProps={{ min: 1 }}
                value={editForm.partySize}
                onChange={e => setEditForm(f => ({ ...f, partySize: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone"
                fullWidth
                value={editForm.phone}
                onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                value={editForm.email}
                onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={editForm.reservationDate}
                onChange={e => setEditForm(f => ({ ...f, reservationDate: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Time"
                fullWidth
                placeholder="7:00 PM"
                value={editForm.reservationTime}
                onChange={e => setEditForm(f => ({ ...f, reservationTime: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Table"
                fullWidth
                value={editForm.tableName}
                onChange={e => setEditForm(f => ({ ...f, tableName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Status"
                fullWidth
                value={editForm.status}
                onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Occasion"
                fullWidth
                value={editForm.occasion}
                onChange={e => setEditForm(f => ({ ...f, occasion: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Special Requests"
                fullWidth
                multiline
                minRows={2}
                value={editForm.specialRequests}
                onChange={e => setEditForm(f => ({ ...f, specialRequests: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateSave}
            disabled={saving}
            sx={{ backgroundColor: '#b71c1c', '&:hover': { backgroundColor: '#7f0000' } }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OpenTableImportsPage;
